import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  OpenerGroup,
  OpenerGroupCategoryRule,
  useCreateGroup,
  usePairCategories,
  useUpdateGroup
} from '../../api/useGroups';
import { categoryKey, conflict409Field } from './groupDialogHelpers';
import { FeatureFlagsSection } from './FeatureFlagsSection';
import { featureFlagsDiffer } from '../../api/useFeatureFlags';
import {
  selectableAccounts,
  useVenueAccounts,
  VenuePlatform
} from '../../api/useVenueAccounts';
import {
  COUNTER_STUCK_SECONDS_LABEL,
  counterStuckSecondsToInput,
  parseCounterStuckSecondsInput
} from '../shared/counterStuckSeconds';
import {
  LADDER_ADVERSE_TICKS_HELP,
  LADDER_ADVERSE_TICKS_LABEL,
  LADDER_SECTION_LABEL,
  LADDER_STEP_MS_HELP,
  LADDER_STEP_MS_LABEL,
  ladderFieldToInput,
  parseLadderAdverseTicksInput,
  parseLadderStepMsInput
} from '../shared/counterHedgeLadder';

const PRESET_COLORS = [
  '#e57373',
  '#ffb74d',
  '#fff176',
  '#81c784',
  '#4fc3f7',
  '#7986cb',
  '#ba68c8',
  '#f06292'
];

type Mode = 'blank' | 'category';

/** Tri-state boolean ↔ select-string. '' = inherit (null), 'on'/'off'. */
const gateToInput = (v: boolean | null | undefined): string =>
  v == null ? '' : v ? 'on' : 'off';
const gateToValue = (s: string): boolean | null =>
  s === 'on' ? true : s === 'off' ? false : null;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Present = edit (rename / color / sortOrder / dynamic); absent = create. */
  group?: OpenerGroup | null;
  /**
   * The group's desired pod state. Feature flags are delivered at pod BOOT, and
   * the engine/feed pods are built on their account's Secrets, so the backend
   * refuses an effective change to either with a 409 while this is on/panicked —
   * the controls disable to match rather than letting the edit bounce.
   */
  podsDesired?: 'on' | 'off' | 'panicked';
}

/**
 * Pull status + message out of an axios rejection so a 409 (duplicate name /
 * duplicate categoryRule) can be shown inline on the right field instead of
 * relying solely on the global mutation-error toast.
 */
function extractApiError(err: unknown): { status?: number; message: string } {
  const e = err as {
    response?: { status?: number; data?: { message?: string | string[] } };
    message?: string;
  };
  const raw = e?.response?.data?.message;
  const message =
    (Array.isArray(raw) ? raw.join('; ') : raw) || e?.message || 'Save failed';
  return { status: e?.response?.status, message };
}

/**
 * Create/edit dialog for opener pair groups. Create offers two modes —
 * Blank (name + optional color) and From category (seeds the group from a
 * category/subcategory bucket, optionally dynamic). Edit keeps the
 * categoryRule read-only and exposes name / color / sortOrder / dynamic /
 * quiet re-eval seconds / counter → market seconds / the hedge-ladder pair.
 * The numeric fields share the blank semantics: blank on EDIT sends explicit
 * null (revert to inherit / off); blank on CREATE omits the key.
 *
 * The two venue accounts are chosen INDEPENDENTLY and are required to create:
 * the backend refuses a group without both, so a group created without them
 * would be unstartable from the moment it exists.
 */
export const CreateGroupDialog = ({
  open,
  onClose,
  group,
  podsDesired
}: Props) => {
  const isEdit = !!group;
  const createMutation = useCreateGroup();
  const updateMutation = useUpdateGroup();

  const [mode, setMode] = useState<Mode>('blank');
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [quietReevalSeconds, setQuietReevalSeconds] = useState('');
  const [stuckHedgeSeconds, setStuckHedgeSeconds] = useState('');
  const [ladderStepMs, setLadderStepMs] = useState('');
  const [ladderAdverseTicks, setLadderAdverseTicks] = useState('');
  const [lockstepAhead, setLockstepAhead] = useState('');
  // Per-platform placement-gate-disable defaults — tri-state encoded as a
  // string select: '' = inherit global (null), 'on' = true, 'off' = false.
  const [polyGate, setPolyGate] = useState('');
  const [kalshiGate, setKalshiGate] = useState('');
  // Group-level Polymarket maker post-only default — same tri-state encoding.
  const [polyPostOnly, setPolyPostOnly] = useState('');
  const [dynamic, setDynamic] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  // '' = nothing chosen. There is NO preselect, not even when the registry holds
  // exactly one account for a venue: a defaulted account is a choice nobody
  // made, and reading a group against an account it never traded fabricates a
  // position the operator cannot tell from a real one.
  const [kalshiAccountId, setKalshiAccountId] = useState('');
  const [polymarketAccountId, setPolymarketAccountId] = useState('');
  const [kalshiAccountError, setKalshiAccountError] = useState<string | null>(
    null
  );
  const [polymarketAccountError, setPolymarketAccountError] = useState<
    string | null
  >(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  const { data: categories, isLoading: categoriesLoading } = usePairCategories(
    open && !isEdit && mode === 'category'
  );
  const {
    data: venueAccounts,
    isLoading: accountsLoading,
    isError: accountsErrored,
  } = useVenueAccounts(open);
  // Keyed on the DATA, not on the status flag alone: react-query's `isError`
  // is sticky and RETAINS the last successful `data`, so a failed BACKGROUND
  // refetch would otherwise print "could not read the account registry" under
  // a select that is fully populated from the earlier success.
  const accountsUnreachable = accountsErrored && !venueAccounts;

  /**
   * The accounts are immutable under live pods — the running engine + feed were
   * built on the OLD account's Secrets, so the backend 409s the change. Fails
   * CLOSED on an unknown pod state for the same reason the flags section does:
   * `undefined` means the pod-state query has not resolved or omitted this
   * group, not that the group is stopped. Create has no pods, so it never locks.
   */
  const accountsLocked = isEdit && podsDesired !== 'off';

  useEffect(() => {
    if (!open) return;
    setMode('blank');
    setName(group?.name ?? '');
    setColor(group?.color ?? '');
    setSortOrder(group?.sortOrder != null ? String(group.sortOrder) : '');
    setQuietReevalSeconds(
      group?.makerQuietReevalSeconds != null
        ? String(group.makerQuietReevalSeconds)
        : ''
    );
    setStuckHedgeSeconds(
      counterStuckSecondsToInput(group?.counterStuckMarketHedgeSeconds)
    );
    setLadderStepMs(ladderFieldToInput(group?.counterHedgeLadderStepMs));
    setLadderAdverseTicks(
      ladderFieldToInput(group?.counterHedgeLadderAdverseTicks)
    );
    setLockstepAhead(
      group?.lockstepMaxAheadBatches != null
        ? String(group.lockstepMaxAheadBatches)
        : ''
    );
    setPolyGate(gateToInput(group?.disablePlacementGatePolymarket));
    setKalshiGate(gateToInput(group?.disablePlacementGateKalshi));
    setPolyPostOnly(gateToInput(group?.polyMakerPostOnly));
    setDynamic(group?.dynamic ?? false);
    setFeatureFlags({ ...(group?.featureFlags ?? {}) });
    setSelectedCategory('');
    // An UNSET account on the edited group stays unset here — it must not be
    // filled in for the operator.
    setKalshiAccountId(group?.kalshiAccountId ?? '');
    setPolymarketAccountId(group?.polymarketAccountId ?? '');
    setKalshiAccountError(null);
    setPolymarketAccountError(null);
    setNameError(null);
    setCategoryError(null);
    setGenericError(null);
  }, [open, group]);

  const selectedRule = useMemo<OpenerGroupCategoryRule | undefined>(() => {
    if (!selectedCategory) return undefined;
    const match = (categories ?? []).find(
      c => categoryKey(c.category, c.subcategory) === selectedCategory
    );
    if (!match) return undefined;
    return {
      category: match.category,
      ...(match.subcategory ? { subcategory: match.subcategory } : {})
    };
  }, [selectedCategory, categories]);

  // Both ladder fields are parsed on every render so the 250 ms floor and the
  // integer/non-negative rules show up as an inline field error at the point of
  // typing. Nothing server-side checks them yet — the backend DTOs do not carry
  // these fields until the engine PR lands — so this is the ONLY validation
  // there is. Blocking Save on an error is what makes this a
  // guard rather than decoration: on EDIT a blank field is sent as an explicit
  // `null` (clear the group fallback), so shipping an unparseable input would
  // turn a typo into "turn the group's ladder off" without saying so.
  const parsedLadderStep = parseLadderStepMsInput(ladderStepMs);
  const parsedLadderAdverse = parseLadderAdverseTicksInput(ladderAdverseTicks);
  const ladderInvalid =
    parsedLadderStep.error !== null || parsedLadderAdverse.error !== null;

  const saving = createMutation.isLoading || updateMutation.isLoading;
  const canSave =
    !!name.trim() &&
    (isEdit || mode === 'blank' || !!selectedRule) &&
    !saving &&
    !ladderInvalid;
  /**
   * A locked edit cannot change the accounts at all, so it does not demand them:
   * a group whose pods are up on an UNSET account (a pre-migration row) must
   * still be renamable. Everywhere else both are mandatory.
   */
  const accountsRequired = !accountsLocked;

  const handleSave = async () => {
    setNameError(null);
    setCategoryError(null);
    setGenericError(null);
    setKalshiAccountError(null);
    setPolymarketAccountError(null);
    // Blocked here rather than by a disabled button: a greyed-out Create says
    // nothing about WHICH field is missing, and the account is the one field
    // this dialog refuses to guess.
    if (accountsRequired) {
      const missingKalshi = !kalshiAccountId;
      const missingPoly = !polymarketAccountId;
      if (missingKalshi) setKalshiAccountError('Pick a Kalshi account');
      if (missingPoly) setPolymarketAccountError('Pick a Polymarket account');
      if (missingKalshi || missingPoly) return;
    }
    const parsedStuck = parseCounterStuckSecondsInput(stuckHedgeSeconds);
    try {
      if (isEdit && group) {
        await updateMutation.mutateAsync({
          id: group._id,
          dto: {
            name: name.trim(),
            color: color || undefined,
            dynamic,
            // ONLY when it actually changed. An unchanged account sent under
            // live pods is refused with a 409 that would sink the whole edit
            // (a rename included) over a field nobody touched.
            ...(kalshiAccountId && kalshiAccountId !== group.kalshiAccountId
              ? { kalshiAccountId }
              : {}),
            ...(polymarketAccountId &&
            polymarketAccountId !== group.polymarketAccountId
              ? { polymarketAccountId }
              : {}),
            ...(sortOrder.trim() !== '' && Number.isFinite(Number(sortOrder))
              ? { sortOrder: Number(sortOrder) }
              : {}),
            // Blank on edit reverts to inherit — send explicit null.
            ...(quietReevalSeconds.trim() === ''
              ? { makerQuietReevalSeconds: null }
              : Number.isFinite(Number(quietReevalSeconds))
                ? { makerQuietReevalSeconds: Number(quietReevalSeconds) }
                : {}),
            // Blank on edit clears the group fallback — explicit null.
            ...(parsedStuck.ok
              ? { counterStuckMarketHedgeSeconds: parsedStuck.value }
              : {}),
            // Explicit-null-on-blank, but ONLY when the operator actually
            // moved the field — the same rule the featureFlags block below
            // applies, for the same reason. `opener-orchestrator` runs
            // `ValidationPipe({ whitelist: true, forbidNonWhitelisted: true })`
            // and its `UpdateGroupDto` does not carry these two keys until the
            // engine PR lands, so emitting them on EVERY save would 400 the
            // whole edit — a rename, a colour, a gate — for a group nobody ever
            // gave a ladder. Clearing a ladder IS a change, so the explicit
            // `null` that turns one off still reaches the wire.
            ...(parsedLadderStep.value !== (group.counterHedgeLadderStepMs ?? null)
              ? { counterHedgeLadderStepMs: parsedLadderStep.value }
              : {}),
            ...(parsedLadderAdverse.value !==
            (group.counterHedgeLadderAdverseTicks ?? null)
              ? { counterHedgeLadderAdverseTicks: parsedLadderAdverse.value }
              : {}),
            ...(lockstepAhead.trim() === ''
              ? { lockstepMaxAheadBatches: null }
              : Number.isInteger(Number(lockstepAhead)) && Number(lockstepAhead) >= 0
                ? { lockstepMaxAheadBatches: Number(lockstepAhead) }
                : {}),
            // Tri-state: inherit ('') sends explicit null on edit so the group
            // reverts to the global default.
            disablePlacementGatePolymarket: gateToValue(polyGate),
            disablePlacementGateKalshi: gateToValue(kalshiGate),
            polyMakerPostOnly: gateToValue(polyPostOnly),
            // ONLY when it changed. Sending it on every save would 400 the
            // whole edit whenever the map holds a key the registry no longer
            // declares — which is exactly the state a flag's retirement window
            // creates, and no control renders for a retired key, so the group
            // would be un-editable until someone ran the migration. It also
            // means this can ship before the backend without breaking edits.
            ...(featureFlagsDiffer(featureFlags, group.featureFlags)
              ? { featureFlags }
              : {})
          }
        });
      } else {
        await createMutation.mutateAsync({
          name: name.trim(),
          kalshiAccountId,
          polymarketAccountId,
          color: color || undefined,
          ...(quietReevalSeconds.trim() !== '' &&
          Number.isFinite(Number(quietReevalSeconds))
            ? { makerQuietReevalSeconds: Number(quietReevalSeconds) }
            : {}),
          ...(parsedStuck.ok && parsedStuck.value != null
            ? { counterStuckMarketHedgeSeconds: parsedStuck.value }
            : {}),
          // Blank on CREATE omits the key (no ladder / engine default ticks),
          // matching every other optional field on this branch.
          ...(parsedLadderStep.value != null
            ? { counterHedgeLadderStepMs: parsedLadderStep.value }
            : {}),
          ...(parsedLadderAdverse.value != null
            ? { counterHedgeLadderAdverseTicks: parsedLadderAdverse.value }
            : {}),
          ...(lockstepAhead.trim() !== '' &&
          Number.isInteger(Number(lockstepAhead)) && Number(lockstepAhead) >= 0
            ? { lockstepMaxAheadBatches: Number(lockstepAhead) }
            : {}),
          // Blank on create omits the key (inherit global); on/off send concrete.
          ...(polyGate !== ''
            ? { disablePlacementGatePolymarket: gateToValue(polyGate) }
            : {}),
          ...(kalshiGate !== ''
            ? { disablePlacementGateKalshi: gateToValue(kalshiGate) }
            : {}),
          ...(polyPostOnly !== ''
            ? { polyMakerPostOnly: gateToValue(polyPostOnly) }
            : {}),
          ...(mode === 'category'
            ? { categoryRule: selectedRule, dynamic }
            : {})
        });
      }
      onClose();
    } catch (err) {
      const { status, message } = extractApiError(err);
      if (status === 409) {
        const field = conflict409Field(message);
        if (field === 'category') setCategoryError(message);
        else if (field === 'general') setGenericError(message);
        else setNameError(message);
      } else {
        setGenericError(message);
      }
    }
  };

  const accountSelect = (
    platform: VenuePlatform,
    label: string,
    value: string,
    onPick: (next: string) => void,
    error: string | null
  ) => {
    const options = selectableAccounts(venueAccounts, platform);
    // The saved account may be absent from the offerable set because it was
    // disabled or removed. It is still what this group trades, so it is
    // rendered (never offerable) rather than silently blanked — but NOT when
    // the registry read failed: there the empty option list says nothing about
    // the account, and the helper text below already names the real cause.
    const savedButUnofferable =
      !accountsUnreachable && !!value && !options.some(a => a.accountId === value);
    return (
      <TextField
        select
        label={label}
        size="small"
        value={value}
        onChange={e => onPick(e.target.value)}
        error={!!error}
        disabled={accountsLocked}
        helperText={
          error ??
          (accountsUnreachable
            ? // Without this the operator reads two empty selects as "this build
              // has no accounts registered" and goes looking in the wrong place.
              'Could not read the account registry — prediction-manager may be down. These are not empty because no accounts exist.'
            : accountsLocked
              ? 'Stop the group to change its account — its engine + feed pods were built on that account’s Secrets.'
              : 'Required, and never defaulted. The group trades this account.')
        }
        SelectProps={{ inputProps: { 'aria-label': label } }}
        fullWidth
      >
        {accountsLoading && (
          <MenuItem disabled value="">
            Loading…
          </MenuItem>
        )}
        {accountsUnreachable && !!value && (
          // Still rendered, so the group's saved account does not vanish from
          // the control — but named for what we actually know.
          <MenuItem disabled value={value}>
            {value} (account registry unreachable)
          </MenuItem>
        )}
        {!accountsLoading && savedButUnofferable && (
          <MenuItem disabled value={value}>
            {value} (not selectable — disabled or unknown)
          </MenuItem>
        )}
        {options.map(a => (
          <MenuItem key={a.accountId} value={a.accountId}>
            {a.name}
            <Typography
              component="span"
              sx={{ fontSize: '0.7rem', color: 'grey.500', ml: 1 }}
            >
              {a.accountId}
            </Typography>
          </MenuItem>
        ))}
      </TextField>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>
        {isEdit ? `Edit group — ${group?.name}` : 'New group'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isEdit && (
            <RadioGroup
              row
              value={mode}
              onChange={e => setMode(e.target.value as Mode)}
            >
              <FormControlLabel
                value="blank"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: '0.8rem' }}>Blank</Typography>
                }
              />
              <FormControlLabel
                value="category"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    From category
                  </Typography>
                }
              />
            </RadioGroup>
          )}

          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={e => {
              setName(e.target.value);
              setNameError(null);
            }}
            error={!!nameError}
            helperText={nameError ?? undefined}
            fullWidth
            autoFocus
          />

          {accountSelect(
            'kalshi',
            'Kalshi account',
            kalshiAccountId,
            next => {
              setKalshiAccountId(next);
              setKalshiAccountError(null);
            },
            kalshiAccountError
          )}

          {accountSelect(
            'polymarket',
            'Polymarket account',
            polymarketAccountId,
            next => {
              setPolymarketAccountId(next);
              setPolymarketAccountError(null);
            },
            polymarketAccountError
          )}

          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'grey.500', mb: 0.5 }}>
              Color (optional — click again to clear)
            </Typography>
            <Stack direction="row" spacing={1}>
              {PRESET_COLORS.map(preset => (
                <Box
                  key={preset}
                  onClick={() => setColor(c => (c === preset ? '' : preset))}
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: preset,
                    cursor: 'pointer',
                    border: theme =>
                      color === preset
                        ? `2px solid ${theme.palette.common.white}`
                        : '2px solid transparent'
                  }}
                />
              ))}
            </Stack>
          </Box>

          {isEdit && (
            <TextField
              label="Sort order"
              size="small"
              type="number"
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}
              fullWidth
            />
          )}

          <TextField
            label="Quiet re-eval seconds"
            size="small"
            type="number"
            value={quietReevalSeconds}
            onChange={e => setQuietReevalSeconds(e.target.value)}
            helperText="Blank = default 30s · 10–20s live sports · up to 300s long-dated"
            fullWidth
          />

          <TextField
            label={COUNTER_STUCK_SECONDS_LABEL}
            size="small"
            type="number"
            value={stuckHedgeSeconds}
            onChange={e => setStuckHedgeSeconds(e.target.value)}
            helperText="Group fallback: counter maker unfilled after N s → cancel + hedge at market. Rule value wins · blank = off"
            fullWidth
          />

          {/* Hedge ladder — the two fields are one block because the second is
              inert without the first: the tick threshold is only consulted
              while a step interval resolves to a value. */}
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 1 }}>
              {LADDER_SECTION_LABEL}
            </Typography>
            <Stack spacing={2}>
              <TextField
                label={LADDER_STEP_MS_LABEL}
                size="small"
                type="number"
                value={ladderStepMs}
                onChange={e => setLadderStepMs(e.target.value)}
                error={parsedLadderStep.error !== null}
                helperText={parsedLadderStep.error ?? LADDER_STEP_MS_HELP}
                fullWidth
              />
              <TextField
                label={LADDER_ADVERSE_TICKS_LABEL}
                size="small"
                type="number"
                value={ladderAdverseTicks}
                onChange={e => setLadderAdverseTicks(e.target.value)}
                error={parsedLadderAdverse.error !== null}
                helperText={
                  parsedLadderAdverse.error ?? LADDER_ADVERSE_TICKS_HELP
                }
                fullWidth
              />
            </Stack>
          </Box>

          <TextField
            label="Lockstep max ahead batches"
            size="small"
            type="number"
            value={lockstepAhead}
            onChange={e => setLockstepAhead(e.target.value)}
            helperText="0 = strict (wait for counter fill). 1 = one batch ahead. Blank = off / inherit. Rule override wins."
            fullWidth
          />

          <TextField
            select
            label="No gate — Polymarket"
            size="small"
            value={polyGate}
            onChange={e => setPolyGate(e.target.value)}
            helperText="Group default for the Polymarket placement gate. Inherit = use global. Rule override wins."
            fullWidth
          >
            <MenuItem value="">— inherit (global) —</MenuItem>
            <MenuItem value="on">On (disable gate)</MenuItem>
            <MenuItem value="off">Off (gate on)</MenuItem>
          </TextField>

          <TextField
            select
            label="No gate — Kalshi"
            size="small"
            value={kalshiGate}
            onChange={e => setKalshiGate(e.target.value)}
            helperText="Group default for the Kalshi placement gate. Inherit = use global. Rule override wins."
            fullWidth
          >
            <MenuItem value="">— inherit (global) —</MenuItem>
            <MenuItem value="on">On (disable gate)</MenuItem>
            <MenuItem value="off">Off (gate on)</MenuItem>
          </TextField>

          <TextField
            select
            label="Poly maker post-only"
            size="small"
            value={polyPostOnly}
            onChange={e => setPolyPostOnly(e.target.value)}
            helperText="On = the Polymarket maker goes out with postOnly: the venue rejects it instead of executing when it would be marketable, and the engine reprices. Inherit = use global. Rule override wins."
            fullWidth
          >
            <MenuItem value="">— inherit (global) —</MenuItem>
            <MenuItem value="on">On (post-only)</MenuItem>
            <MenuItem value="off">Off (may cross)</MenuItem>
          </TextField>

          {isEdit && (
            <FeatureFlagsSection
              value={featureFlags}
              onChange={setFeatureFlags}
              resolved={group?.resolvedFeatureFlags}
              savedValue={group?.featureFlags}
              // Fail CLOSED: `undefined` means the pod-state query has not
              // resolved, errored, or omitted this group (the payload is
              // fail-soft). Enabling the controls on unknown state invites an
              // edit the backend will 409.
              locked={podsDesired !== 'off'}
              lockReason={
                podsDesired === undefined
                  ? 'Pod state unknown — flags are locked until it is known the group is stopped.'
                  : 'Stop the group to change flags — the set is delivered to the engine pod at boot.'
              }
            />
          )}

          {isEdit && group?.categoryRule && (
            <Typography sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
              Category rule: {group.categoryRule.category}
              {group.categoryRule.subcategory
                ? ` / ${group.categoryRule.subcategory}`
                : ''}{' '}
              (read-only)
            </Typography>
          )}

          {!isEdit && mode === 'category' && (
            <TextField
              select
              label="Category / subcategory"
              size="small"
              value={selectedCategory}
              onChange={e => {
                setSelectedCategory(e.target.value);
                setCategoryError(null);
              }}
              error={!!categoryError}
              helperText={categoryError ?? undefined}
              fullWidth
            >
              {categoriesLoading && (
                <MenuItem disabled value="">
                  Loading…
                </MenuItem>
              )}
              {(categories ?? []).map(c => (
                <MenuItem
                  key={categoryKey(c.category, c.subcategory)}
                  value={categoryKey(c.category, c.subcategory)}
                >
                  {c.category}
                  {c.subcategory ? ` / ${c.subcategory}` : ''} ({c.pairCount}{' '}
                  pairs)
                </MenuItem>
              ))}
            </TextField>
          )}

          {(isEdit ? !!group?.categoryRule : mode === 'category') && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={dynamic}
                  onChange={e => setDynamic(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '0.8rem' }}>Dynamic</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
                    Dynamic groups auto-assign future pairs matching the
                    category; fixed groups only assign current matches.
                  </Typography>
                </Box>
              }
            />
          )}

          {genericError && (
            <Typography sx={{ fontSize: '0.75rem', color: 'error.main' }}>
              {genericError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          size="small"
        >
          {saving ? (
            <CircularProgress size={16} />
          ) : isEdit ? (
            'Save'
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
